// A one off note from the team to one member, written by hand in the admin.
// The preview shows this member's real values, not samples.

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Eye, Loader2, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { HOUSE_FROM_EMAIL, HOUSE_FROM_NAME } from "@/lib/contentBlocks";
import { fillPreviewTokens, renderEmailPreview } from "@/lib/emailPreview";
import { useSendCooldown } from "@/components/admin/UserAccessActions";

const PORTAL_URL = "https://wlthystwrd.com";

export interface CustomEmailRecord {
  id: string;
  email?: string | null;
  first_name?: string | null;
  una_name?: string | null;
  tracking_number?: string | null;
}

export default function SendCustomEmailDialog({
  open,
  onOpenChange,
  record,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  record: CustomEmailRecord;
}) {
  const [fromName, setFromName] = useState("");
  const [replyTo, setReplyTo] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [busy, setBusy] = useState(false);
  const { cooling, remaining, start } = useSendCooldown();

  useEffect(() => {
    if (open) {
      setFromName("");
      setReplyTo("");
      setSubject("");
      setBody("");
      setShowPreview(true);
    }
  }, [open]);

  const to = record.email || "";

  const vars = useMemo<Record<string, string>>(
    () => ({
      first_name: record.first_name || "there",
      una_name: record.una_name || "your Association",
      email: to,
      portal_url: PORTAL_URL,
      tracking_number: record.tracking_number || "",
    }),
    [record.first_name, record.una_name, record.tracking_number, to],
  );

  const hasDash = /[\u2013\u2014]/.test(subject) || /[\u2013\u2014]/.test(body);
  const canSend = Boolean(to) && subject.trim().length > 0 && body.trim().length > 0 && !hasDash;

  const send = async () => {
    setBusy(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-member-actions", {
        body: {
          member_record_id: record.id,
          action: "custom_email",
          subject: subject.trim(),
          body,
          from_name: fromName.trim(),
          reply_to: replyTo.trim(),
        },
      });
      if (error) {
        let message = "Please try again.";
        try {
          const parsed = await (error as { context?: Response }).context?.json();
          if (parsed?.error) message = parsed.error;
        } catch (_e) {
          // keep the plain message
        }
        toast.error("We could not send that email", { description: message });
        return;
      }
      start();
      if (data?.sent) {
        toast.success("Email sent", { description: `Sent to ${to}.` });
      } else {
        toast.success("Email queued", {
          description: "The mail service was not reachable, so it is held and will go out.",
        });
      }
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send an email</DialogTitle>
          <DialogDescription>
            A one off note from the team to this member. It goes out through the same
            pipe as every other email we send.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1">
            <Label>To</Label>
            <Input value={to || "No email on file"} readOnly disabled />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="custom-from">Sender name</Label>
              <Input
                id="custom-from"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder={HOUSE_FROM_NAME}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="custom-reply">Reply to email</Label>
              <Input
                id="custom-reply"
                type="email"
                value={replyTo}
                onChange={(e) => setReplyTo(e.target.value)}
                placeholder={HOUSE_FROM_EMAIL}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="custom-subject">Subject</Label>
            <Input
              id="custom-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="A short, plain subject"
            />
          </div>

          <div className="space-y-1">
            <Label htmlFor="custom-body">Body</Label>
            <Textarea
              id="custom-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              placeholder="Write the note the way you would say it out loud."
            />
            <p className="text-xs text-muted-foreground">
              You can use these in the subject or the body: {"{{first_name}}"},{" "}
              {"{{una_name}}"}, {"{{email}}"}, {"{{portal_url}}"}, {"{{tracking_number}}"}.
              They fill in with this member's own values.
            </p>
          </div>

          {hasDash && (
            <p className="text-sm text-destructive">
              Dashes are not house style. Use a period or a comma.
            </p>
          )}

          {showPreview && (
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="rounded-md border">
                <div className="border-b p-3">
                  <p className="text-sm font-semibold">
                    {fillPreviewTokens(fromName, vars) || HOUSE_FROM_NAME}
                  </p>
                  <p className="text-sm">
                    {fillPreviewTokens(subject, vars) || "No subject yet"}
                  </p>
                </div>
                <div
                  className="rounded-b-md bg-white p-4"
                  dangerouslySetInnerHTML={{
                    __html: renderEmailPreview("", body, subject, "", vars),
                  }}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setShowPreview((v) => !v)}>
            <Eye className="h-4 w-4 mr-2" />
            {showPreview ? "Hide preview" : "Preview"}
          </Button>
          <Button disabled={!canSend || busy || cooling} onClick={() => void send()}>
            {busy ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {cooling ? `Wait ${remaining}s` : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
